import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from '@guildeducationinc/guild-auth';
import { datadogRum } from '@datadog/browser-rum';
import {
  ENROLLMENT_ROUTES,
  UserEnrollment,
  useEnrollmentService,
} from '@src/api/enrollmentServiceClient';
import { ComponentDataKey } from '../components/Pages/Home/Layout/config';

interface EnrollmentsContextInterface {
  loading: boolean;
  enrollments: UserEnrollment[];
  enrollmentsRefetch?: () => void;
}

const EnrollmentsContext = createContext<EnrollmentsContextInterface>({
  loading: true,
  enrollments: [],
});

const EnrollmentsProvider: React.FC = ({ children }) => {
  const { user, getAccessToken, initialized } = useAuth();
  const [loading, setLoading] = useState(true);
  const [enrollments, setEnrollments] = useState<UserEnrollment[]>([]);
  const { get } = useEnrollmentService(getAccessToken, ENROLLMENT_ROUTES.V1_ENROLLMENT_INTENTIONS);

  const getEnrollments = useCallback(async () => {
    try {
      if (user?.id) {
        const response = await get({ user_uuid: user?.id });
        setEnrollments(response.usersEnrollments || []);
        setLoading(false);
      }
    } catch (error: any) {
      datadogRum.addError(error, {
        message: `There was an error getting enrollments for a user's applications: ${error.message}`,
      });
    }
  }, [get, user]);

  useEffect(() => {
    if (user?.id && initialized) getEnrollments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, initialized]);

  return (
    <EnrollmentsContext.Provider
      value={{
        loading,
        enrollments,
        enrollmentsRefetch: getEnrollments,
      }}
    >
      {children}
    </EnrollmentsContext.Provider>
  );
};

const useEnrollmentsData = (key?: ComponentDataKey) => {
  const context = useContext(EnrollmentsContext);

  /* istanbul ignore if */
  if (context === undefined) {
    throw new Error('useEnrollmentData must be called within a EnrollmentsProvider.');
  }

  return context;
};

export default EnrollmentsProvider;
export { useEnrollmentsData, EnrollmentsContext, EnrollmentsContextInterface };
